import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Sparkles, PartyPopper, MessageSquare } from "lucide-react";

export default function RecapDialog({ language, isoFriday, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr(""); setData(null);
    api.get(`/lessons/recap/${language}/${isoFriday}`)
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => {
        if (!alive) return;
        setErr(e.response?.data?.detail === "recap_not_ready"
          ? "This week's recap unlocks on Friday. Keep going!"
          : (e.response?.data?.detail || "Couldn't load recap"));
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [language, isoFriday]);

  const readableDate = new Date(isoFriday + "T00:00:00").toLocaleDateString(undefined, {
    weekday: "long", month: "long", day: "numeric", year: "numeric",
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-lg bg-card border rounded-2xl p-0 overflow-hidden animate-slide-up"
        data-testid="recap-dialog"
      >
        <div className="bg-secondary text-secondary-foreground px-6 py-5 relative overflow-hidden">
          <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-honey/25 blur-2xl pointer-events-none" />
          <DialogHeader className="relative z-10">
            <div className="text-xs uppercase tracking-widest text-secondary-foreground/70 flex items-center gap-2">
              <PartyPopper className="w-3.5 h-3.5 text-honey" strokeWidth={2} /> Friday recap &middot; {readableDate}
            </div>
            <DialogTitle className="font-display text-2xl mt-1 text-secondary-foreground">
              Your week in one page
            </DialogTitle>
            <DialogDescription className="text-secondary-foreground/70 sr-only">Weekly language recap</DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-6 py-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center py-10 text-muted-foreground gap-2">
              <Loader2 className="w-5 h-5 animate-spin" /> Weaving your week together&hellip;
            </div>
          )}
          {err && <div className="text-sm text-muted-foreground text-center py-6" data-testid="recap-error">{err}</div>}

          {data && !loading && (
            <>
              <div className="text-center space-y-2">
                <h3 className="font-display text-2xl tracking-tight text-terracotta" data-testid="recap-headline">
                  {data.recap.headline}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed max-w-md mx-auto" data-testid="recap-summary">
                  {data.recap.summary}
                </p>
              </div>

              <div className="space-y-2" data-testid="recap-word-list">
                <div className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5 text-honey" strokeWidth={2} /> Your five words
                </div>
                <div className="grid grid-cols-1 gap-2">
                  {data.week_words.map((w, i) => (
                    <div
                      key={w.date}
                      className="flex items-center justify-between rounded-xl border border-border bg-card px-4 py-3"
                      data-testid={`recap-word-${i}`}
                    >
                      <div>
                        <div className="font-display text-lg leading-none">{w.word}</div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {new Date(w.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "short" })}
                        </div>
                      </div>
                      <div className="text-sm text-muted-foreground">{w.translation}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-honey/10 border border-honey/30 rounded-xl p-4 space-y-1">
                <div className="text-xs uppercase tracking-widest text-honey flex items-center gap-2">
                  <MessageSquare className="w-3.5 h-3.5" strokeWidth={2} /> Put it into context
                </div>
                <p className="text-sm leading-relaxed" data-testid="recap-context">
                  {data.recap.context_prompt}
                </p>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
